/**
 * Firebase App initialisation — singleton module.
 * Imported once in app.config.ts via `provideFirebase()`.
 *
 * When `environment.useEmulators` is true, all Firebase services
 * connect to the local emulator suite instead of production servers.
 */
import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  InjectionToken,
} from '@angular/core';
import { initializeApp, getApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  Auth,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator,
} from 'firebase/functions';
import {
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import {
  getStorage,
  FirebaseStorage,
  connectStorageEmulator,
} from 'firebase/storage';
import { environment } from '@/environments/environment';

// ─── Injection Tokens ──────────────────────────────────────────────
export const FIREBASE_APP = new InjectionToken<FirebaseApp>('FIREBASE_APP');
export const FIREBASE_AUTH = new InjectionToken<Auth>('FIREBASE_AUTH');
export const FIREBASE_FUNCTIONS = new InjectionToken<Functions>(
  'FIREBASE_FUNCTIONS'
);
export const FIREBASE_FIRESTORE = new InjectionToken<Firestore>(
  'FIREBASE_FIRESTORE'
);
export const FIREBASE_STORAGE = new InjectionToken<FirebaseStorage>(
  'FIREBASE_STORAGE'
);

// ─── Emulator Ports (must match clubezy-firebase-1225/firebase.json) ───
const EMULATOR_HOST = 'localhost';
const EMULATOR_PORTS = {
  auth: 9099,
  functions: 5001,
  firestore: 8080,
  storage: 9199,
} as const;

/** Where the SDK's local (cross-restart) auth persistence keeps the user. */
const LEGACY_AUTH_DB = 'firebaseLocalStorageDb';
const LEGACY_AUTH_KEY_PREFIX = 'firebase:authUser:';

/**
 * Remove any sign-in left in cross-restart storage by builds that used the
 * default local persistence. Session persistence never reads it, but a shared
 * computer would otherwise keep the last member's refresh token on disk
 * indefinitely. Best-effort; sessionStorage (the live session) is untouched.
 */
export function purgePersistedAuth(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(LEGACY_AUTH_KEY_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing to purge.
  }
  try {
    globalThis.indexedDB?.deleteDatabase(LEGACY_AUTH_DB);
  } catch {
    // IndexedDB unavailable — nothing to purge.
  }
}

/**
 * Per-tab sign-in: the session lives in sessionStorage and ends when the tab
 * closes. This app is visited briefly — to register, verify an email, reset a
 * password — often on shared devices (a family computer, a club's
 * registration-day laptop). With the default local persistence a sign-in
 * outlived the visit, so the next person to open a registration link was
 * silently signed in as the last one, with their details and children
 * prefilled. The popup resolver is what getAuth() would have supplied; the
 * Google/Apple popups need it.
 *
 * The options are their own exported constant so a spec can name what this app
 * signs in with. Note a spec cannot check it against the REAL SDK: under the
 * node build every persistence export is the same value, so the assertion
 * would hold whichever one was picked. firebase.provider.spec therefore mocks
 * `firebase/auth` AND re-imports this module from a cleared registry — mocking
 * alone is not enough, because a copy of this module another spec in the same
 * worker already loaded comes back from cache still bound to the real SDK.
 */
export const TAB_SCOPED_AUTH_OPTIONS = {
  persistence: browserSessionPersistence,
  popupRedirectResolver: browserPopupRedirectResolver,
} as const;

export function initTabScopedAuth(app: FirebaseApp): Auth {
  try {
    return initializeAuth(app, TAB_SCOPED_AUTH_OPTIONS);
  } catch {
    // Auth was already initialised for this app (e.g. HMR / repeat call).
    return getAuth(app);
  }
}

// ─── Provider Factory ──────────────────────────────────────────────
export function provideFirebase(): EnvironmentProviders {
  const app = getApps().length ? getApp() : initializeApp(environment.firebase);
  purgePersistedAuth();
  const auth = initTabScopedAuth(app);
  const functions = getFunctions(app, 'australia-southeast1');
  const firestore = getFirestore(app);
  const storage = getStorage(app);

  // Load Google Maps Places script dynamically in the browser.
  // Mirrors clubezy-administration-0526 firebase.provider.ts — without this the
  // AddressFieldComponent used by the public registration renderer finds no
  // `google.maps.places` and silently degrades to a plain text input (CLUB-1426).
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const scriptId = 'google-maps-places-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.firebase.apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  // Connect to emulators when running locally
  if (environment.useEmulators) {
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
      disableWarnings: true,
    });
    connectFunctionsEmulator(
      functions,
      EMULATOR_HOST,
      EMULATOR_PORTS.functions
    );
    connectFirestoreEmulator(
      firestore,
      EMULATOR_HOST,
      EMULATOR_PORTS.firestore
    );
    connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);

    console.log(
      `🔥 Firebase emulators connected — Auth:${EMULATOR_PORTS.auth} ` +
        `Functions:${EMULATOR_PORTS.functions} Firestore:${EMULATOR_PORTS.firestore} ` +
        `Storage:${EMULATOR_PORTS.storage}`
    );
  }

  return makeEnvironmentProviders([
    { provide: FIREBASE_APP, useValue: app },
    { provide: FIREBASE_AUTH, useValue: auth },
    { provide: FIREBASE_FUNCTIONS, useValue: functions },
    { provide: FIREBASE_FIRESTORE, useValue: firestore },
    { provide: FIREBASE_STORAGE, useValue: storage },
  ]);
}
